/**
 * Lead Room screen (Sprint 3a) - fake reading rooms + the back-alley info dealer.
 * Sub-screen of the exchange tab. Rooms post "calls" you can follow; their
 * HIDDEN accuracy is never shown - you learn it from the call history (hit/miss).
 * VIP rooms are gated behind a rewarded ad. The info dealer sells vague hints
 * for game money. Text-only, no emoji.
 */
import { LEAD_ROOMS, getLeadRoom } from '../game/config/leadRooms.js';
import { INFO_PRODUCTS, infoProductCost } from '../game/config/infoShop.js';
import { COINS } from '../game/config/coins.js';
import type { RoomCall, RoomRuntime, InfoHint } from '../game/leadRoomState.js';
import { getCurrentCPS } from '../game/state.js';
import { formatNumber } from '../game/utils.js';
import { t, tv } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { AdManager, getAdRewards } from '../monetization/AdManager.js';
import { coinIconHtml } from './coinIcon.js';
import { roomChatHtml, bindRoomChatEvents } from './roomChatPanel.js';
import { setLeadScreenActive } from '../game/systems/chatterSystem.js';
import {
  onRender, getState, getViewState, dispatch, setViewState, setInnerHTML,
  markScreenDirty, requestRender, showToast,
} from './renderer.js';

let container: HTMLDivElement | null = null;
const shownHooks = new Set<string>();

export function initLeadRoomScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;
  container = document.createElement('div');
  container.id = 'leadroom-screen';
  container.className = 'screen-panel exchange-theme';
  content.appendChild(container);
  onRender(renderLeadRooms, { alwaysTabs: ['leadroom'] });
}

function coinName(coinId: string): string {
  const cfg = COINS.find(c => c.id === coinId);
  return cfg ? t(cfg.nameKey) : coinId;
}

function coinIconOf(coinId: string): string {
  const cfg = COINS.find(c => c.id === coinId);
  return cfg ? coinIconHtml(cfg, true) : '';
}

/** Static call phrasing: call_up_N / call_down_N with {coin} interpolation. */
function callText(call: RoomCall): string {
  const dir = call.claimedDirection === 1 ? 'up' : 'down';
  return tv(`call_${dir}_${call.variant}`, { coin: coinName(call.coinId) });
}

/** History as hit/miss chips (newest first) - lets the player judge the room. */
function historyHtml(room: RoomRuntime): string {
  if (room.history.length === 0) return `<span class="lr-hist-empty">${t('lr_no_history')}</span>`;
  return room.history
    .map(c => {
      const cls = c.status === 'hit' ? 'lr-hit' : 'lr-miss';
      const label = c.status === 'hit' ? t('lr_hit') : t('lr_miss');
      return `<span class="lr-hist-chip ${cls}">${label}</span>`;
    })
    .join('');
}

function activeCallHtml(room: RoomRuntime): string {
  if (!room.activeCall) return `<div class="lr-call lr-call-idle">${t('lr_no_call')}</div>`;
  const call = room.activeCall;
  const dir = call.claimedDirection === 1
    ? '<span class="ex-profit">&#9650;</span>'
    : '<span class="ex-loss">&#9660;</span>';
  return `<div class="lr-call">
    <span class="lr-call-icon">${coinIconOf(call.coinId)}</span>
    <span class="lr-call-text">${callText(call)}</span>
    <span class="lr-call-dir">${dir}</span>
  </div>`;
}

function roomLockedHtml(roomId: string): string {
  if (!shownHooks.has(`vip_room:${roomId}`)) {
    shownHooks.add(`vip_room:${roomId}`);
    dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'vip_room', event: 'shown' } });
  }
  return `<button class="hook-btn hook-btn-sm lr-vip-btn" data-vip="${roomId}">
    <span class="hook-main">${t('lr_vip_enter')}</span>
    <span class="hook-sub">${t('lr_vip_sub')}</span>
  </button>`;
}

function renderRoom(roomId: string): string {
  const cfg = getLeadRoom(roomId);
  const room = getState().leadRooms.rooms[roomId];
  if (!cfg || !room) return '';
  const now = Date.now();
  const locked = cfg.vip && now >= room.unlockedUntil;

  const vipTag = cfg.vip ? `<span class="lr-vip-tag">${t('lr_vip_tag')}</span>` : '';
  let inner: string;
  if (locked) {
    inner = `<div class="lr-locked">${t('lr_locked_hint')}</div>${roomLockedHtml(roomId)}`;
  } else {
    const unlockLeft = cfg.vip && room.unlockedUntil !== Number.MAX_SAFE_INTEGER
      ? `<span class="lr-vip-timer">${Math.max(0, Math.ceil((room.unlockedUntil - now) / 60000))}m</span>`
      : '';
    inner = `${activeCallHtml(room)}
      <div class="lr-hist-row"><span class="lr-hist-label">${t('lr_track_record')}</span>${historyHtml(room)}${unlockLeft}</div>
      ${roomChatHtml(roomId, room)}`;
  }

  return `<div class="lr-room" style="border-left:3px solid ${cfg.color}">
    <div class="lr-room-head">
      <span class="lr-room-name">${t(cfg.nameKey)}</span>${vipTag}
    </div>
    <div class="lr-room-tag">${t(cfg.taglineKey)}</div>
    ${inner}
  </div>`;
}

/** Hint payload -> human line (deliberately vague). */
function hintLine(hint: InfoHint): string {
  if (hint.productId === 'whale_hint' && hint.coinId) {
    return tv('shop_hint_whale', { coin: coinName(hint.coinId) });
  }
  if (hint.productId === 'news_tip' && hint.coinId) {
    return tv('shop_hint_news', { coin: coinName(hint.coinId) });
  }
  if (hint.productId === 'room_leak' && hint.roomId) {
    const cfg = getLeadRoom(hint.roomId);
    const band = t(`shop_band_${hint.band}`);
    return tv('shop_hint_room', { room: cfg ? t(cfg.nameKey) : hint.roomId, band });
  }
  return '';
}

function renderDealer(): string {
  const st = getState();
  const cps = getCurrentCPS(st);
  const now = Date.now();

  let html = `<div class="ex-section-title">${t('shop_title')}</div>
    <div class="lr-dealer-intro">${t('shop_intro')}</div>`;

  // active hints
  const hints = st.leadRooms.hints.filter(h => now < h.expiresAt);
  if (hints.length > 0) {
    html += '<div class="lr-hint-list">';
    for (const h of hints) html += `<div class="lr-hint">${hintLine(h)}</div>`;
    html += '</div>';
  }

  html += '<div class="lr-shop-list">';
  for (const p of INFO_PRODUCTS) {
    const cost = infoProductCost(p, cps);
    const disabled = st.coins < cost;
    html += `<button class="lr-shop-item${disabled ? ' lr-shop-disabled' : ''}" data-buy="${p.id}" ${disabled ? 'disabled' : ''}>
      <div class="lr-shop-main">
        <span class="lr-shop-name">${t(p.nameKey)}</span>
        <span class="lr-shop-desc">${t(p.descKey)}</span>
      </div>
      <span class="lr-shop-cost">${formatNumber(cost)}</span>
    </button>`;
  }
  return html + '</div>';
}

function renderLeadRooms() {
  if (!container) return;
  // rooms tick fast + seed only while this screen is the active tab
  setLeadScreenActive(getViewState().currentTab === 'leadroom');

  let html = `<div class="ex-detail-head">
    <button class="ex-back-btn" data-action="back">&lt; ${t('ex_back')}</button>
    <div class="ex-detail-title">${t('lr_title')}</div>
  </div>
  <div class="lr-intro">${t('lr_intro')}</div>`;

  html += '<div class="lr-room-list">';
  for (const cfg of LEAD_ROOMS) html += renderRoom(cfg.id);
  html += '</div>';
  html += renderDealer();

  if (setInnerHTML(container, html)) {
    bindEvents();
    bindRoomChatEvents(container);
  }
}

function bindEvents() {
  if (!container) return;
  container.querySelector('[data-action="back"]')?.addEventListener('click', () => {
    playSfx('tap');
    setViewState({ currentTab: 'exchange' });
  });
  container.querySelectorAll('[data-vip]').forEach(btn => {
    btn.addEventListener('click', () => void onVipEnter((btn as HTMLElement).dataset.vip!));
  });
  container.querySelectorAll('[data-buy]').forEach(btn => {
    btn.addEventListener('click', () => onBuyInfo((btn as HTMLElement).dataset.buy!));
  });
}

async function onVipEnter(roomId: string) {
  dispatch({ type: 'AD_HOOK_LOG', payload: { hookId: 'vip_room', event: 'accepted' } });
  const result = await AdManager.showRewardedAd(getAdRewards().VIP_ROOM);
  if (result) {
    dispatch({ type: 'VIP_ROOM_ENTER', payload: { roomId } });
    playSfx('achievement');
    showToast(t('lr_vip_unlocked'));
  }
  markScreenDirty();
  requestRender();
}

function onBuyInfo(productId: string) {
  const before = getState().leadRooms.hints.length;
  const coinsBefore = getState().coins;
  dispatch({ type: 'BUY_INFO', payload: { productId } });
  const after = getState();
  if (after.leadRooms.hints.length > before || after.coins < coinsBefore) {
    playSfx('purchase');
    showToast(t('shop_bought'));
  } else {
    playSfx('error');
    showToast(t('shop_no_info'));
  }
  markScreenDirty();
  requestRender();
}
